import React, { useState } from 'react';
import { Button, Alert, Spinner, Modal, Form, Dropdown, ButtonGroup } from 'react-bootstrap';
import { machineService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function MachineTable({ machines, onAction, onEdit, onDelete }) {
  const { hasPermission } = useAuth();
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [machineStatuses, setMachineStatuses] = useState({});
  const [pingInProgress, setPingInProgress] = useState({});
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [revealMachine, setRevealMachine] = useState(null);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealError, setRevealError] = useState(null);
  const [revealedData, setRevealedData] = useState(null);

  // Ping verification
  const verifyMachineStatus = async (machine) => {
    setPingInProgress(prev => ({ ...prev, [machine.id]: true }));
    try {
      const data = await machineService.ping(machine.id);
      const isOnline = data.isOnline;

      setMachineStatuses(prev => ({
        ...prev,
        [machine.id]: {
          isOnline,
          lastPing: new Date(),
          responseTime: data.responseTime || null
        }
      }));

      // Update machine status in database if different
      const currentActive = isOnline ? 1 : 0;
      if (machine.is_active !== currentActive) {
        await machineService.updateStatus(machine.id, currentActive);
      }
    } catch (error) {
      console.error('Ping failed:', error);
      setMachineStatuses(prev => ({
        ...prev,
        [machine.id]: {
          isOnline: false,
          lastPing: new Date(),
          responseTime: null,
          error: true
        }
      }));
    } finally {
      setPingInProgress(prev => ({ ...prev, [machine.id]: false }));
    }
  };

  const maskIp = (ip) => {
    if (!ip) return 'hidden';
    const parts = ip.split('.');
    if (parts.length !== 4) return 'hidden';
    return `***.***.***.${parts[3]}`;
  };

  const maskMac = (mac) => {
    if (!mac) return 'hidden';
    const parts = mac.split(':');
    if (parts.length < 6) return '••:••:••:••:••:••';
    return `${parts[0]}:${parts[1]}:XX:XX:XX:${parts[5]}`;
  };


  const openRevealModal = (machine) => {
    setRevealMachine(machine);
    setRevealedData(null);
    setRevealPassword('');
    setRevealError(null);
    setShowRevealModal(true);
  };

  const handleRevealSubmit = async () => {
    if (!revealMachine) return;
    setRevealLoading(true);
    setRevealError(null);
    try {
      const data = await machineService.reveal(revealMachine.id, revealPassword);
      setRevealedData(data);
    } catch (err) {
      console.error('Reveal failed', err);
      setRevealError(err.response?.data?.error || 'Reveal failed');
    } finally {
      setRevealLoading(false);
    }
  };

  const handleMachineSelect = (machineId, isSelected) => {
    setSelectedMachines(prev =>
      isSelected
        ? [...prev, machineId]
        : prev.filter(id => id !== machineId)
    );
  };

  const getMachineStatus = (machine) => {
    const pingStatus = machineStatuses[machine.id];
    if (pingStatus) {
      return pingStatus.isOnline;
    }
    return machine.is_active === 1;
  };

  const getStatusBadge = (machine) => {
    const isOnline = getMachineStatus(machine);
    const pingStatus = machineStatuses[machine.id];
    const isPinging = pingInProgress[machine.id];

    if (isPinging) {
      return (
        <span className="status-badge status-checking">
          <Spinner size="sm" animation="border" className="me-1" style={{ width: '10px', height: '10px' }} />
          Checking...
        </span>
      );
    }

    if (pingStatus?.error) {
      return (
        <span className="status-badge status-offline" title="Ping request failed">
          Offline
        </span>
      );
    }

    return isOnline ? (
      <span className="status-badge status-online" title={pingStatus?.responseTime ? `Response time: ${pingStatus.responseTime}ms` : 'Online'}>
        Online {pingStatus?.responseTime && `(${pingStatus.responseTime}ms)`}
      </span>
    ) : (
      <span className="status-badge status-offline">
        Offline
      </span>
    );
  };

  return (
    <div className="machine-table-container">
      {selectedMachines.length > 0 && (
        <div className="p-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ backgroundColor: 'rgba(59, 130, 246, 0.05)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-layer-group text-theme-highlight"></i>
            <span className="text-theme-main font-medium" style={{ fontSize: '0.9rem' }}>
              Bulk Operations: <strong>{selectedMachines.length}</strong> selected
            </span>
          </div>
          <div className="d-flex gap-2">
            <Button
              className="btn-premium-primary py-1 px-3"
              style={{ fontSize: '0.8rem' }}
              onClick={() => onAction(selectedMachines, 'wake')}
            >
              <i className="fas fa-play me-1"></i> Wake
            </Button>
            <Button
              className="btn-premium-accent py-1 px-3"
              style={{ fontSize: '0.8rem' }}
              onClick={() => onAction(selectedMachines, 'restart')}
            >
              <i className="fas fa-redo me-1"></i> Restart
            </Button>
            <Button
              className="btn-premium-danger py-1 px-3"
              style={{ fontSize: '0.8rem' }}
              onClick={() => onAction(selectedMachines, 'shutdown')}
            >
              <i className="fas fa-power-off me-1"></i> Shutdown
            </Button>
          </div>
        </div>
      )}

      <div className="custom-table-container">
        <table className="table-custom">
          <thead>
            <tr>
              <th style={{ width: '48px', textAlign: 'center' }}>
                <Form.Check
                  type="checkbox"
                  id="select-all-machines"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedMachines(machines.map(m => m.id));
                    } else {
                      setSelectedMachines([]);
                    }
                  }}
                  checked={selectedMachines.length === machines.length && machines.length > 0}
                />
              </th>
              <th>Machine Name</th>
              <th>IP & MAC Credentials</th>
              <th>Status
                <Button
                  variant="link"
                  size="sm"
                  className="ms-2 p-0 text-theme-muted"
                  onClick={() => machines.forEach(machine => verifyMachineStatus(machine))}
                  disabled={Object.values(pingInProgress).some(Boolean)}
                  title="Ping check all machines"
                >
                  {Object.values(pingInProgress).some(Boolean) ? (
                    <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} />
                  ) : (
                    <i className="fas fa-sync-alt"></i>
                  )}
                </Button>
              </th>
              <th style={{ width: '120px', text: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {machines.map((machine) => (
              <tr key={machine.id}>
                <td style={{ textAlign: 'center' }}>
                  <Form.Check
                    type="checkbox"
                    id={`machine-select-${machine.id}`}
                    onChange={(e) => handleMachineSelect(machine.id, e.target.checked)}
                    checked={selectedMachines.includes(machine.id)}
                  />
                </td>
                <td>
                  <div className="d-flex flex-column">
                    <span className="text-theme-main font-semibold" style={{ fontSize: '0.95rem' }}>
                      {machine.name}
                    </span>
                    <span className="text-theme-muted" style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                      <i className={`fas fa-circle me-1 ${machine.connection_status === 'connected' ? 'text-success' : 'text-danger'}`} style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}></i>
                      Agent: {machine.connection_status === 'connected' ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="d-flex flex-column gap-1">
                    <span style={{ fontSize: '0.8rem' }}>
                      <span className="text-theme-muted">IP:</span> <code className="text-theme-highlight font-semibold">{maskIp(machine.ip_address)}</code>
                    </span>
                    <span style={{ fontSize: '0.8rem' }}>
                      <span className="text-theme-muted">MAC:</span> <code className="text-theme-muted">{maskMac(machine.mac_address)}</code>
                    </span>
                    {hasPermission('manage_assets') && (
                      <Button
                        variant="link"
                        className="p-0 text-start text-theme-highlight"
                        style={{ fontSize: '0.75rem', width: 'fit-content', textDecoration: 'none' }}
                        onClick={() => openRevealModal(machine)}
                      >
                        <i className="fas fa-eye me-1"></i>Reveal connection details
                      </Button>
                    )}
                  </div>
                </td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    {getStatusBadge(machine)}
                    <Button
                      variant="link"
                      className="p-0 text-theme-muted"
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => verifyMachineStatus(machine)}
                      disabled={pingInProgress[machine.id]}
                    >
                      {pingInProgress[machine.id] ? (
                        <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
                      ) : (
                        <i className="fas fa-redo-alt" title="Check connection"></i>
                      )}
                    </Button>
                  </div>
                </td>
                <td>
                  <Dropdown as={ButtonGroup} size="sm">
                    <Button
                      onClick={() => onAction([machine.id], 'wake')}
                      className="btn-premium-primary py-1 px-2.5"
                      style={{ fontSize: '0.8rem' }}
                      disabled={!hasPermission('power')}
                    >
                      <i className="fas fa-play me-1"></i>Wake
                    </Button>
                    <Dropdown.Toggle split className="btn-premium-primary py-1" disabled={!hasPermission('power') && !hasPermission('remote_view') && !hasPermission('manage_assets') && !hasPermission('kiosk')} />
                    <Dropdown.Menu style={{ background: 'var(--dropdown-bg)', border: '1px solid var(--border-color)' }}>
                      {hasPermission('power') && (
                        <>
                          <Dropdown.Item onClick={() => onAction([machine.id], 'shutdown')} className="text-danger py-2">
                            <i className="fas fa-power-off me-2"></i>Shutdown
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => onAction([machine.id], 'restart')} className="text-warning py-2">
                            <i className="fas fa-redo me-2"></i>Restart
                          </Dropdown.Item>
                        </>
                      )}
                      {hasPermission('remote_view') && (
                        <Dropdown.Item onClick={() => onAction([machine.id], 'mirror')} className="text-info py-2">
                          <i className="fas fa-tv me-2"></i>Mirror Screen
                        </Dropdown.Item>
                      )}
                      {hasPermission('kiosk') && (
                        <Dropdown.Item onClick={() => onAction([machine.id], 'kiosk')} className="text-theme-main py-2">
                          <i className="fas fa-window-maximize me-2"></i>Kiosk Control
                        </Dropdown.Item>
                      )}
                      {((hasPermission('power') || hasPermission('remote_view') || hasPermission('kiosk')) && hasPermission('manage_assets')) && (
                        <Dropdown.Divider style={{ borderColor: 'var(--border-color)' }} />
                      )}
                      {hasPermission('manage_assets') && (
                        <>
                          <Dropdown.Item onClick={() => onEdit(machine)} className="text-theme-main py-2">
                            <i className="fas fa-edit me-2"></i>Edit Configurations
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => onDelete(machine.id)} className="text-danger py-2">
                            <i className="fas fa-trash me-2"></i>Remove Asset
                          </Dropdown.Item>
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {machines.length === 0 && (
        <div className="glass-card text-center py-5 mt-3 d-flex flex-column align-items-center">
          <i className="fas fa-desktop text-theme-muted mb-3" style={{ fontSize: '3rem' }}></i>
          <h5 className="text-theme-main mb-1">No network assets found</h5>
          <p className="text-theme-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Click 'Add Machine' to register network units.
          </p>
        </div>
      )}

      {/* Reveal Password Modal */}
      <Modal show={showRevealModal} onHide={() => setShowRevealModal(false)} centered>
        <div className="modal-content-premium">
          <Modal.Header closeButton className="modal-header-premium border-0">
            <Modal.Title style={{ fontWeight: 700 }}>
              <i className="fas fa-shield-alt me-2 text-primary"></i>
              Authorization Required
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 py-3">
            {revealError && (
              <Alert variant="danger" className="border-0 mb-3" style={{ background: 'rgba(244, 63, 94, 0.15)', color: '#fb7185' }}>
                {revealError}
              </Alert>
            )}

            {!revealedData ? (
              <Form onSubmit={(e) => { e.preventDefault(); handleRevealSubmit(); }}>
                <Form.Group>
                  <Form.Label className="form-label-premium text-uppercase tracking-wider">Reveal Password</Form.Label>
                  <Form.Control
                    type="password"
                    className="form-input-premium w-100"
                    value={revealPassword}
                    onChange={(e) => setRevealPassword(e.target.value)}
                    placeholder="Enter credential reveal key"
                    autoFocus
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Provide the security view password configured on the server.
                  </Form.Text>
                </Form.Group>
              </Form>
            ) : (
              <div className="d-flex flex-column gap-3">
                <div style={{ background: 'rgba(120, 120, 120, 0.05)', padding: '1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                  <div className="mb-2.5">
                    <span className="text-theme-muted text-uppercase d-block" style={{ fontSize: '0.7rem', fontWeight: 600 }}>Target Host IP</span>
                    <code className="text-theme-main font-bold" style={{ fontSize: '1rem' }}>{revealedData.ip_address}</code>
                  </div>
                  <div>
                    <span className="text-theme-muted text-uppercase d-block" style={{ fontSize: '0.7rem', fontWeight: 600 }}>MAC Address</span>
                    <code className="text-theme-main font-bold" style={{ fontSize: '1rem' }}>{revealedData.mac_address}</code>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="modal-footer-premium border-0 gap-2">
            <Button className="btn-premium-secondary" onClick={() => setShowRevealModal(false)}>
              Close
            </Button>
            {!revealedData && (
              <Button className="btn-premium-primary" onClick={handleRevealSubmit} disabled={revealLoading || !revealPassword}>
                {revealLoading ? <Spinner animation="border" size="sm" /> : 'Confirm Reveal'}
              </Button>
            )}
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
}

export default MachineTable;